import { useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/shared/ui/badge';
import type { ListColumn } from '@/shared/app-list-layout/types';
import type { DataTableView } from '@/shared/app-list-layout';
import { formatDate } from '@/shared/format';
import { agreementsApi } from '@/modules/agreements/services/api';
import {
  AgreementsList,
  executionStatusLabel,
  requiredActionLabel,
  verificationPolicyLabel,
} from '@/modules/agreements/shared';
import type { MyAgreementRequirementResponse } from '@/api/schemas/agreements.types';

/**
 * A requirement as the list sees it: the response plus the WORD for its state.
 *
 * The API reports `satisfied` as a boolean, but a filter and a tab both speak in
 * words — "Action required", not "false". Deriving the word once, here, means the
 * column filter, the saved views and the search box all narrow by the same value
 * the user reads on screen.
 */
type RequirementRow = MyAgreementRequirementResponse & { status: 'satisfied' | 'outstanding' };

/**
 * The subject's own agreements.
 *
 * This is the ONLY subject-facing list of obligations. There is no subject-facing
 * list-executions route, so signed-PDF history is built from this list — each row carries
 * `executionId` and `executionStatus` — and each execution detail is then read by id.
 *
 * A row that is `satisfied: false` is an OUTSTANDING obligation. The console shows it as
 * such and offers the action the requirement demands.
 *
 * The page renders through the shared `AppListLayout` with the same surface the tenant
 * directory carries: saved-view tabs with counts, a search box, column-bound filters,
 * numeric pagination and the table/cards toggle. The read is a whole-collection read —
 * `listMyRequirements` takes no query parameters — so the page answers the engine's
 * query locally, and `onFetch` stays the single data path.
 */

/** The obligation states a subject can filter by. */
const STATUS_OPTIONS = [
  { value: 'satisfied', label: 'Satisfied' },
  { value: 'outstanding', label: 'Action required' },
];

/** The assurance levels a requirement can demand. */
const VERIFICATION_OPTIONS = [
  { value: 'fresh_strong', label: 'Fresh identity check required' },
  { value: 'session_reattested', label: 'Session re-attestation' },
];

/**
 * The fields a search term is matched against — what identifies an obligation to a human.
 *
 * The status is matched by its WORD, not by `satisfied`'s boolean: a user types
 * "outstanding", never "false".
 */
function searchFields(row: RequirementRow): ReadonlyArray<string> {
  return [
    requiredActionLabel(row.requiredAction),
    verificationPolicyLabel(row.verificationPolicy),
    row.satisfied ? 'Satisfied' : 'Action required',
  ];
}

/**
 * The caution banner, derived from the whole-collection counts.
 *
 * It counts every outstanding obligation the subject has, not just the ones on the
 * current page — a warning that under-reported because of pagination would be worse
 * than no warning at all.
 */
function outstandingNotice(counts: Partial<Record<string, number>>): ReactNode {
  const outstanding = counts.outstanding ?? 0;
  if (outstanding === 0) return null;

  return (
    <div className="rounded-lg border border-caution bg-caution-soft px-4 py-3 text-sm">
      <p className="font-medium text-content">
        {outstanding} agreement{outstanding === 1 ? '' : 's'} require your action.
      </p>
      <p className="text-content-muted">
        Normal business operations are blocked until these are completed.
      </p>
    </div>
  );
}

/** Saved views rendered as tabs above the obligation list. */
function buildRequirementViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All agreements', count: counts.all },
    {
      id: 'outstanding',
      label: 'Outstanding',
      filters: { status: 'outstanding' },
      count: counts.outstanding,
    },
    {
      id: 'satisfied',
      label: 'Satisfied',
      filters: { status: 'satisfied' },
      count: counts.satisfied,
    },
  ];
}

/** Satisfied obligations say so in words, never by colour alone. */
function RequirementStatusBadge({ satisfied }: { satisfied: boolean }): ReactNode {
  return satisfied ? (
    <Badge tone="positive">Satisfied</Badge>
  ) : (
    <Badge tone="caution">Action required</Badge>
  );
}

/**
 * The execution cell: a requirement with no execution has no history to open, and a
 * requirement with one opens the execution itself.
 */
function executionCell(
  requirement: RequirementRow,
  onOpen: (requirement: RequirementRow) => void,
): ReactNode {
  if (!requirement.executionId) return <span className="text-content-muted">—</span>;

  return (
    <button
      type="button"
      className="text-left font-medium text-accent hover:underline"
      onClick={() => {
        onOpen(requirement);
      }}
    >
      {requirement.executionStatus ? executionStatusLabel(requirement.executionStatus) : 'Open'}
    </button>
  );
}

/** The page's columns. Declarative descriptors — the engine renders every cell. */
function requirementColumns(
  onOpen: (requirement: RequirementRow) => void,
): ReadonlyArray<ListColumn<RequirementRow>> {
  return [
    {
      id: 'action',
      header: 'Action',
      // The obligation's action is what identifies the row — it is the heading a
      // card leads with, not a labelled field buried under one.
      primary: true,
      sortable: true,
      cell: (row) => requiredActionLabel(row.requiredAction),
    },
    {
      id: 'verification',
      header: 'Verification',
      filter: { type: 'select', options: VERIFICATION_OPTIONS, placeholder: 'Any' },
      cell: (row) => (
        <span className="text-content-muted">{verificationPolicyLabel(row.verificationPolicy)}</span>
      ),
    },
    {
      id: 'deadline',
      header: 'Deadline',
      sortable: true,
      cell: (row) => (
        <span className="text-content-muted">{row.deadlineAt ? formatDate(row.deadlineAt) : '—'}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      filter: { type: 'select', options: STATUS_OPTIONS, placeholder: 'Any' },
      cell: (row) => <RequirementStatusBadge satisfied={row.satisfied} />,
    },
    {
      id: 'execution',
      header: 'Execution',
      cell: (row) => executionCell(row, onOpen),
    },
  ];
}

export function RequirementsListPage(): ReactNode {
  const navigate = useNavigate();

  const read = useCallback(
    () =>
      agreementsApi.listMyRequirements('tenant').then(({ requirements }) =>
        requirements.map((requirement): RequirementRow => ({
          ...requirement,
          status: requirement.satisfied ? 'satisfied' : 'outstanding',
        })),
      ),
    [],
  );

  const onOpen = useCallback(
    (requirement: RequirementRow): void => {
      if (!requirement.executionId) return;
      // A completed execution has nothing left to sign — it opens its RECORD, which carries the
      // executed PDF. Anything still outstanding opens the signing surface.
      if (requirement.executionStatus === 'completed') {
        void navigate({
          to: '/agreements/executions/$executionId/record',
          params: { executionId: requirement.executionId },
        });
        return;
      }
      void navigate({
        to: '/agreements/executions/$executionId',
        params: { executionId: requirement.executionId },
      });
    },
    [navigate],
  );
  const columns = useMemo(() => requirementColumns(onOpen), [onOpen]);

  return (
    <AgreementsList<RequirementRow>
      title="Agreements"
      description="Your agreement obligations, and the action each one asks of you."
      read={read}
      searchFields={searchFields}
      countKeyOf={(row) => row.status}
      buildViews={buildRequirementViews}
      buildBanner={outstandingNotice}
      columns={columns}
      getRowId={(row) => row.requirementId}
      // Five columns run off a phone screen, so the page asks for the responsive
      // presentation: cards below `sm`, the table above.
      responsive
      emptyTitle="No agreements"
      emptyDescription="This workspace has no agreement obligations."
      noMatchDescription="No obligation matches this tab, search or filter."
    />
  );
}
